import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Input, RegularModal, Toggle } from '@/components_deprecated'
import React from 'react'
import styled from 'styled-components'
import { Field, Form } from '..'

export interface DynamicColumnForm {
  name: string
  displayName: string
  enabled: boolean
}

interface EditDynamicColumnModalProps {
  column: DynamicColumnForm
  /**
   * The header the table renders while the column has no display name. Given
   * one, the name field is optional: left empty, the column keeps that
   * translated header instead of being frozen to one language.
   */
  defaultDisplayName?: string
  disableToggle: boolean
  lockedByModule?: boolean
  lockedValue?: boolean
  onSubmit: PromptResolve<DynamicColumnForm | null>
}

const DescriptionLabel = styled.label`
  color: ${props => props.theme.colors.gray3} !important;
`
const ErrorLabel = styled.div`
  color: ${props => props.theme.colors.red};
  font-size: 0.8rem;
  margin: 0.5rem 0 6px 6px;
`

export const EditDynamicColumnModal: React.FC<EditDynamicColumnModalProps> = ({
  column,
  defaultDisplayName,
  disableToggle,
  lockedByModule = false,
  lockedValue,
  onSubmit,
}) => {
  const translations = useTranslate({
    editColumn: 'settings.edit-dynamic-column',
    name: 'common.name',
    enabled: 'common.enabled',
    disabled: 'common.disabled',
    showColumn: 'settings.show-column',
    showColumnDescription: 'settings.show-dynamic-column-description',
    showColumnError: 'settings.show-dynamic-column-error',
    showColumnLockedByModule: 'settings.show-dynamic-column-locked-by-module',
    update: 'common.update',

    validation: {
      required: 'common.required',
    },
  })
  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<DynamicColumnForm>({
    values:
      lockedByModule && lockedValue !== undefined
        ? { ...column, enabled: lockedValue }
        : column ?? {
            displayName: '',
            enabled: true,
          },
    validators: {
      displayName: val =>
        val || defaultDisplayName ? null : translations.validation.required,
    },
    config: {
      initAsInvalid: false,
    },
  })

  return (
    <RegularModal
      title={translations.editColumn}
      width="400px"
      submitText={translations.update}
      submitDisabled={!formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={submit(onSubmit)}
    >
      <Form>
        <Field>
          <label>{translations.name}</label>
          <Input
            value={form.displayName}
            error={errors.displayName}
            placeholder={defaultDisplayName}
            autoFocus
            fullWidth
            onChange={handler('displayName')}
          />
        </Field>

        <Field alignLeft>
          <label>{translations.showColumn}</label>
          <DescriptionLabel>
            {translations.showColumnDescription}
          </DescriptionLabel>
          <Toggle
            value={form.enabled}
            thumbBackground={form.enabled ? 'secondary' : 'gray8'}
            disabled={disableToggle}
            offIconProps={{
              icon: 'eye-slash',
              size: '20px',
            }}
            onIconProps={{
              icon: 'eye',
              size: '20px',
              color: form.enabled ? 'gray11' : 'gray5',
            }}
            onChange={handler('enabled')}
          />
          {disableToggle && (
            <ErrorLabel>
              {lockedByModule
                ? translations.showColumnLockedByModule
                : translations.showColumnError}
            </ErrorLabel>
          )}
        </Field>
      </Form>
    </RegularModal>
  )
}
